import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Shield, Users, MessageSquare, Package, TrendingUp, AlertCircle, CheckCircle, XCircle, Mail, Crown, Trash2, Building2, BarChart3, FileText, Image, Sparkles, RefreshCw, Save } from 'lucide-react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '../utils';
import { safeArray } from '@/components/utils/safeArray';
import LegalDocumentEditor from '@/components/admin/LegalDocumentEditor';
import BannerManager from '@/components/admin/BannerManager';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

export default function Admin() {
  const [user, setUser] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('user');
  const [manufacturerEmail, setManufacturerEmail] = useState('');
  const [selectedProductForAssignment, setSelectedProductForAssignment] = useState(null);
  const [selectedManufacturerForAssignment, setSelectedManufacturerForAssignment] = useState('');
  const [newUserForm, setNewUserForm] = useState({ full_name: '', email: '', role: 'user' });
  const [newManufacturerForm, setNewManufacturerForm] = useState({ name: '', brand: '', website: '', contact_email: '' });
  const [newProfessionalForm, setNewProfessionalForm] = useState({ 
    full_name: '', 
    email: '', 
    professional_type: '', 
    professional_license: '', 
    professional_specialty: '', 
    professional_bio: '' 
  });
  const [editingProfessional, setEditingProfessional] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editingManufacturer, setEditingManufacturer] = useState(null);
  const [settingsForm, setSettingsForm] = useState({});
  const queryClient = useQueryClient();

  useEffect(() => {
    const loadUser = async () => {
      try {
        const currentUser = await base44.auth.me();
        setUser(currentUser);
        if (currentUser.role !== 'admin') {
          toast.error('Acceso denegado');
        }
      } catch (error) {
        console.error('Error loading user:', error);
      }
    };
    loadUser();
  }, []);

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const data = await base44.entities.User.list('-created_date');
      return safeArray(data);
    },
  });

  const { data: posts = [] } = useQuery({
    queryKey: ['posts'],
    queryFn: async () => {
      const data = await base44.entities.Post.list('-created_date');
      return safeArray(data);
    },
  });

  const { data: comments = [] } = useQuery({
    queryKey: ['comments'],
    queryFn: async () => {
      const data = await base44.entities.Comment.list('-created_date');
      return safeArray(data);
    },
  });

  const { data: products = [] } = useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const data = await base44.entities.Product.list('-created_date');
      return safeArray(data);
    },
  });

  const { data: additives = [] } = useQuery({
    queryKey: ['additives'],
    queryFn: async () => {
      const data = await base44.entities.Additive.list('-created_date');
      return safeArray(data);
    },
  });

  const { data: manufacturers = [] } = useQuery({
      queryKey: ['manufacturers'],
      queryFn: async () => {
        const data = await base44.entities.Manufacturer.list('-created_date');
        return safeArray(data);
      },
  });

  const { data: legalDocs = [] } = useQuery({
      queryKey: ['legal-documents'],
      queryFn: async () => {
        const data = await base44.entities.LegalDocument.list();
        return safeArray(data);
      },
  });

  const { data: banners = [] } = useQuery({
      queryKey: ['banners'],
      queryFn: async () => {
        const data = await base44.entities.Banner.list('-created_date');
        return safeArray(data);
      },
  });

  const { data: appSettings } = useQuery({
      queryKey: ['app-settings'],
      queryFn: async () => {
        const settings = safeArray(await base44.entities.AppSettings.filter({ key: 'main' }));
        return settings[0];
      },
  });

  // Sync settingsForm when appSettings loads
  useEffect(() => {
    if (appSettings) setSettingsForm(appSettings);
  }, [appSettings?.id]);

  const { data: vitamins = [] } = useQuery({
      queryKey: ['vitamins'],
      queryFn: async () => {
        const data = await base44.entities.Vitamin.list('-created_date');
        return safeArray(data);
      },
  });

  const updatePostMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Post.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      toast.success('Post actualizado');
    },
  });

  const deletePostMutation = useMutation({
    mutationFn: (id) => base44.entities.Post.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      toast.success('Post eliminado');
    },
  });

  const updateCommentMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Comment.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments'] });
      toast.success('Comentario actualizado');
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: (id) => base44.entities.Comment.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments'] });
      toast.success('Comentario eliminado');
    },
  });

  const handleInviteUser = async () => {
    if (!inviteEmail) {
      toast.error('Ingresa un email');
      return;
    }
    try {
      await base44.users.inviteUser(inviteEmail, inviteRole);
      
      // Enviar email con mensaje personalizado si está configurado
      if (appSettings?.invitation_message) {
        try {
          await base44.integrations.Core.SendEmail({
            from_name: appSettings.app_name || 'ScanEat',
            to: inviteEmail,
            subject: '¡Bienvenido a ScanEat!',
            body: appSettings.invitation_message
          });
        } catch (emailError) {
          console.error('Error sending custom invitation email:', emailError);
        }
      }
      
      toast.success('Invitación enviada');
      setInviteEmail('');
    } catch (error) {
      toast.error('Error al enviar invitación');
    }
  };

  const handleInviteManufacturer = async (method) => {
    if (!manufacturerEmail) {
      toast.error('Ingresa un email');
      return;
    }
    try {
      await base44.users.inviteUser(manufacturerEmail, 'user');
      
      if (method === 'whatsapp') {
        const text = `¡Hola! Has sido invitado a gestionar tu perfil de fabricante en ScanEat. Regístrate aquí: ${window.location.origin}`;
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
      }
      
      toast.success('Invitación enviada al fabricante');
      setManufacturerEmail('');
    } catch (error) {
      toast.error('Error al enviar invitación');
    }
  };

  const assignProductMutation = useMutation({
    mutationFn: async ({ productId, manufacturerId }) => {
      const manufacturer = safeArray(manufacturers).find(m => m.id === manufacturerId);
      if (!manufacturer) throw new Error('Fabricante no encontrado');
      
      await base44.entities.Product.update(productId, {
        brand: manufacturer.brand,
        manufacturer_url: manufacturer.website
      });
      
      await base44.entities.Manufacturer.update(manufacturerId, {
        product_count: (manufacturer.product_count || 0) + 1
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['manufacturers'] });
      setSelectedProductForAssignment(null);
      setSelectedManufacturerForAssignment('');
      toast.success('Producto asignado al fabricante');
    },
  });

  const createUserMutation = useMutation({
    mutationFn: async (userData) => {
      await base44.users.inviteUser(userData.email, userData.role);
      return userData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setNewUserForm({ full_name: '', email: '', role: 'user' });
      toast.success('Usuario registrado exitosamente');
    },
  });

  const createManufacturerMutation = useMutation({
    mutationFn: (data) => base44.entities.Manufacturer.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manufacturers'] });
      setNewManufacturerForm({ name: '', brand: '', website: '', contact_email: '' });
      toast.success('Fabricante registrado exitosamente');
    },
  });

  const createProfessionalMutation = useMutation({
    mutationFn: async (data) => {
      await base44.users.inviteUser(data.email, 'user');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setNewProfessionalForm({ 
        full_name: '', 
        email: '', 
        professional_type: '', 
        professional_license: '', 
        professional_specialty: '', 
        professional_bio: '' 
      });
      toast.success('Profesional registrado exitosamente');
    },
  });

  const updateProfessionalMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.User.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setEditingProfessional(null);
      toast.success('Profesional actualizado');
    },
  });

  const deleteProfessionalMutation = useMutation({
    mutationFn: (id) => base44.entities.User.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Profesional eliminado');
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.User.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setEditingUser(null);
      toast.success('Usuario actualizado');
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (id) => base44.entities.User.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Usuario eliminado');
    },
  });

  const updateManufacturerMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Manufacturer.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manufacturers'] });
      setEditingManufacturer(null);
      toast.success('Fabricante actualizado');
    },
  });

  const deleteManufacturerMutation = useMutation({
    mutationFn: (id) => base44.entities.Manufacturer.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manufacturers'] });
      toast.success('Fabricante eliminado');
    },
  });

  const saveSettingsMutation = useMutation({
    mutationFn: async (data) => {
      if (appSettings?.id) {
        return await base44.entities.AppSettings.update(appSettings.id, data);
      } else {
        return await base44.entities.AppSettings.create({ ...data, key: 'main' });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-settings'] });
      toast.success('Configuración guardada');
    },
  });

  const syncVitaminsMutation = useMutation({
    mutationFn: async () => {
      let synced = 0;
      let created = 0;
      
      for (const product of safeArray(products)) {
        const productVitamins = safeArray(product.vitamins);
        if (productVitamins.length > 0) {
          for (const vitamin of productVitamins) {
            try {
              const existing = safeArray(await base44.entities.Vitamin.filter({ code: vitamin.code }));
              
              if (existing.length > 0) {
                const existingVitamin = existing[0];
                const productIds = safeArray(existingVitamin.product_ids);
                if (!productIds.includes(product.id)) {
                  await base44.entities.Vitamin.update(existingVitamin.id, {
                    product_ids: [...productIds, product.id],
                    name: vitamin.name || existingVitamin.name,
                    description: vitamin.description || existingVitamin.description,
                    benefit_level: vitamin.benefit_level || existingVitamin.benefit_level
                  });
                  synced++;
                }
              } else {
                await base44.entities.Vitamin.create({
                  code: vitamin.code,
                  name: vitamin.name,
                  description: vitamin.description,
                  benefit_level: vitamin.benefit_level,
                  product_ids: [product.id]
                });
                created++;
              }
            } catch (err) {
              console.error('Error syncing vitamin:', err);
            }
          }
        }
      }
      
      return { synced, created };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['vitamins'] });
      toast.success(`Vitaminas sincronizadas: ${result.created} creadas, ${result.synced} actualizadas`);
    },
    onError: () => {
      toast.error('Error al sincronizar vitaminas');
    }
  });

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Acceso Denegado</h2>
          <p className="text-gray-600">No tienes permisos de administrador</p>
        </Card>
      </div>
    );
  }

  const pendingPosts = safeArray(posts).filter(p => p.status === 'pending');
  const pendingComments = safeArray(comments).filter(c => c.status === 'pending');
  const flaggedPosts = safeArray(posts).filter(p => p.flagged_for_medical_advice);
  const flaggedComments = safeArray(comments).filter(c => c.flagged_for_medical_advice);

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 via-white to-gray-50 pb-20">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-gray-900 text-lg">Panel de Administración</h1>
                <p className="text-xs text-gray-400">Gestión completa de ScanEat</p>
              </div>
            </div>
            <Link to={createPageUrl('Analytics')}>
              <Button variant="outline" size="sm" className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                <span className="hidden sm:inline">Analíticas</span>
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-blue-600" />
              <p className="text-sm text-gray-500">Usuarios</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{safeArray(users).length}</p>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="w-4 h-4 text-green-600" />
              <p className="text-sm text-gray-500">Posts</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{safeArray(posts).length}</p>
            {safeArray(pendingPosts).length > 0 && (
              <p className="text-xs text-amber-600 mt-1">{safeArray(pendingPosts).length} pendientes</p>
            )}
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-4 h-4 text-emerald-600" />
              <p className="text-sm text-gray-500">Productos</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{safeArray(products).length}</p>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-purple-600" />
              <p className="text-sm text-gray-500">Aditivos</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{safeArray(additives).length}</p>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-emerald-600" />
              <p className="text-sm text-gray-500">Vitaminas</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{safeArray(vitamins).length}</p>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="users" className="w-full">
          <TabsList className="grid w-full grid-cols-9 mb-6">
            <TabsTrigger value="users">Usuarios</TabsTrigger>
            <TabsTrigger value="professionals">Profesionales</TabsTrigger>
            <TabsTrigger value="manufacturers">Fabricantes</TabsTrigger>
            <TabsTrigger value="register">Registrar</TabsTrigger>
            <TabsTrigger value="moderation">
              Moderación
              {(safeArray(pendingPosts).length + safeArray(pendingComments).length) > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                  {safeArray(pendingPosts).length + safeArray(pendingComments).length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="content">Contenido</TabsTrigger>
            <TabsTrigger value="legal">Legal</TabsTrigger>
            <TabsTrigger value="banners">Banners</TabsTrigger>
            <TabsTrigger value="settings">Configuración</TabsTrigger>
          </TabsList>

          {/* Professionals Tab */}
          <TabsContent value="professionals" className="space-y-4">
            <Card className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Gestión de Profesionales de la Salud</h3>
              
              <h4 className="font-semibold text-gray-700 mb-3 mt-6">Pendientes de Verificación</h4>
              <div className="space-y-3">
                {safeArray(users).filter(u => u.is_professional && !u.professional_verified).map((professional) => (
                  <div key={professional.id} className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        {professional.profile_picture ? (
                          <img src={professional.profile_picture} alt={professional.full_name} className="w-12 h-12 rounded-full object-cover" />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                            {professional.full_name?.[0]?.toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{professional.full_name}</p>
                          <p className="text-sm text-gray-500">{professional.email}</p>
                          <div className="flex gap-2 mt-2 text-xs">
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                              {professional.professional_type}
                            </span>
                            {professional.professional_license && (
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full">
                                Matrícula: {professional.professional_license}
                              </span>
                            )}
                            {professional.professional_specialty && (
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                                {professional.professional_specialty}
                              </span>
                            )}
                          </div>
                          {professional.professional_bio && (
                            <p className="text-xs text-gray-600 mt-2">{professional.professional_bio}</p>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={async () => {
                          try {
                            await base44.entities.User.update(professional.id, { professional_verified: true });
                            queryClient.invalidateQueries({ queryKey: ['users'] });
                            toast.success('Profesional verificado');
                          } catch (error) {
                            toast.error('Error al verificar');
                          }
                        }}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Verificar
                      </Button>
                    </div>
                  </div>
                ))}
                {safeArray(users).filter(u => u.is_professional && !u.professional_verified).length === 0 && (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No hay profesionales pendientes de verificación</p>
                  </div>
                )}
              </div>
              
              <h4 className="font-semibold text-gray-700 mb-3 mt-6">Profesionales Verificados</h4>
              <div className="space-y-3">
                {safeArray(users).filter(u => u.is_professional && u.professional_verified).map((professional) => (
                  <div key={professional.id} className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-3 flex-1">
                      {professional.profile_picture ? (
                        <img src={professional.profile_picture} alt={professional.full_name} className="w-12 h-12 rounded-full object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                          {professional.full_name?.[0]?.toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900">{professional.full_name}</p>
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        </div>
                        <p className="text-sm text-gray-500">{professional.email}</p>
                        <div className="flex gap-2 mt-1 text-xs flex-wrap">
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                            {professional.professional_type}
                          </span>
                          {professional.professional_license && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full">
                              MP: {professional.professional_license}
                            </span>
                          )}
                          {professional.professional_specialty && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                              {professional.professional_specialty}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingProfessional(professional)}
                      >
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (confirm('¿Estás seguro de eliminar este profesional?')) {
                            deleteProfessionalMutation.mutate(professional.id);
                          }
                        }}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {safeArray(users).filter(u => u.is_professional && u.professional_verified).length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">No hay profesionales verificados</p>
                )}
              </div>
            </Card>
          </TabsContent>

          {/* Manufacturers Tab */}
          <TabsContent value="manufacturers" className="space-y-4">
            <Card className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Invitar Fabricante</h3>
              <div className="flex gap-3 mb-4">
                <Input
                  type="email"
                  value={manufacturerEmail}
                  onChange={(e) => setManufacturerEmail(e.target.value)}
                  placeholder="email@fabricante.com"
                  className="flex-1"
                />
                <Button onClick={() => handleInviteManufacturer('email')} variant="outline">
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </Button>
                <Button onClick={() => handleInviteManufacturer('whatsapp')} className="bg-green-600 hover:bg-green-700">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Asignar Producto a Fabricante</h3>
              <div className="flex gap-3">
                <select
                  value={selectedProductForAssignment?.id || ''}
                  onChange={(e) => {
                    const product = safeArray(products).find(p => p.id === e.target.value);
                    setSelectedProductForAssignment(product);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg"
                >
                  <option value="">Seleccionar producto...</option>
                  {safeArray(products).map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <select
                  value={selectedManufacturerForAssignment}
                  onChange={(e) => setSelectedManufacturerForAssignment(e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg"
                >
                  <option value="">Seleccionar fabricante...</option>
                  {safeArray(manufacturers).map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
                <Button 
                  onClick={() => assignProductMutation.mutate({
                    productId: selectedProductForAssignment?.id,
                    manufacturerId: selectedManufacturerForAssignment
                  })}
                  disabled={!selectedProductForAssignment || !selectedManufacturerForAssignment}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  Asignar
                </Button>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Fabricantes Registrados</h3>
              <div className="space-y-3">
                {safeArray(manufacturers).map((m) => (
                  <div key={m.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3 flex-1">
                      {m.logo_url ? (
                        <img src={m.logo_url} alt={m.name} className="w-12 h-12 rounded-lg object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-white" />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{m.name}</p>
                        {m.brand && <p className="text-sm text-gray-500">{m.brand}</p>}
                        <div className="flex items-center gap-2 mt-1">
                          {m.verified && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                              <CheckCircle className="w-3 h-3" />
                              Verificado
                            </span>
                          )}
                          <span className="text-xs text-gray-500">{m.product_count || 0} productos</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingManufacturer(m)}
                      >
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (confirm('¿Estás seguro de eliminar este fabricante?')) {
                            deleteManufacturerMutation.mutate(m.id);
                          }
                        }}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Edit Manufacturer Modal */}
            {editingManufacturer && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="w-full max-w-md p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Editar Fabricante</h3>
                  <div className="space-y-3">
                    <Input
                      placeholder="Nombre del fabricante"
                      value={editingManufacturer.name || ''}
                      onChange={(e) => setEditingManufacturer({ ...editingManufacturer, name: e.target.value })}
                    />
                    <Input
                      placeholder="Marca"
                      value={editingManufacturer.brand || ''}
                      onChange={(e) => setEditingManufacturer({ ...editingManufacturer, brand: e.target.value })}
                    />
                    <Input
                      placeholder="Sitio web"
                      value={editingManufacturer.website || ''}
                      onChange={(e) => setEditingManufacturer({ ...editingManufacturer, website: e.target.value })}
                    />
                    <Input
                      type="email"
                      placeholder="Email de contacto"
                      value={editingManufacturer.contact_email || ''}
                      onChange={(e) => setEditingManufacturer({ ...editingManufacturer, contact_email: e.target.value })}
                    />
                    <div className="flex gap-2 pt-2">
                      <Button
                        variant="outline"
                        onClick={() => setEditingManufacturer(null)}
                        className="flex-1"
                      >
                        Cancelar
                      </Button>
                      <Button
                        onClick={() => updateManufacturerMutation.mutate({
                          id: editingManufacturer.id,
                          data: {
                            name: editingManufacturer.name,
                            brand: editingManufacturer.brand,
                            website: editingManufacturer.website,
                            contact_email: editingManufacturer.contact_email
                          }
                        })}
                        disabled={!editingManufacturer.name || !editingManufacturer.brand}
                        className="flex-1 bg-blue-600 hover:bg-blue-700"
                      >
                        Guardar
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Register Tab */}
          <TabsContent value="register" className="space-y-4">
            <Card className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Registrar Usuario</h3>
              <div className="space-y-3">
                <Input
                  placeholder="Nombre completo"
                  value={newUserForm.full_name}
                  onChange={(e) => setNewUserForm({ ...newUserForm, full_name: e.target.value })}
                />
                <Input
                  type="email"
                  placeholder="Email"
                  value={newUserForm.email}
                  onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                />
                <select
                  value={newUserForm.role}
                  onChange={(e) => setNewUserForm({ ...newUserForm, role: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                >
                  <option value="user">Usuario</option>
                  <option value="admin">Admin</option>
                </select>
                <Button 
                  onClick={() => createUserMutation.mutate(newUserForm)}
                  disabled={!newUserForm.full_name || !newUserForm.email}
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                >
                  Registrar Usuario
                </Button>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4">
                {editingProfessional ? 'Editar Profesional' : 'Registrar Profesional'}
              </h3>
              <div className="space-y-3">
                <Input
                  placeholder="Nombre completo"
                  value={editingProfessional ? editingProfessional.full_name : newProfessionalForm.full_name}
                  onChange={(e) => editingProfessional 
                    ? setEditingProfessional({ ...editingProfessional, full_name: e.target.value })
                    : setNewProfessionalForm({ ...newProfessionalForm, full_name: e.target.value })
                  }
                />
                <Input
                  type="email"
                  placeholder="Email"
                  value={editingProfessional ? editingProfessional.email : newProfessionalForm.email}
                  onChange={(e) => editingProfessional 
                    ? setEditingProfessional({ ...editingProfessional, email: e.target.value })
                    : setNewProfessionalForm({ ...newProfessionalForm, email: e.target.value })
                  }
                  disabled={!!editingProfessional}
                />
                <select
                  value={editingProfessional ? editingProfessional.professional_type : newProfessionalForm.professional_type}
                  onChange={(e) => editingProfessional 
                    ? setEditingProfessional({ ...editingProfessional, professional_type: e.target.value })
                    : setNewProfessionalForm({ ...newProfessionalForm, professional_type: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                >
                  <option value="">Seleccionar tipo</option>
                  <option value="nutricionista">Nutricionista</option>
                  <option value="medico">Médico</option>
                  <option value="enfermero">Enfermero</option>
                  <option value="farmaceutico">Farmacéutico</option>
                  <option value="otro">Otro</option>
                </select>
                <Input
                  placeholder="Número de matrícula/licencia"
                  value={editingProfessional ? editingProfessional.professional_license : newProfessionalForm.professional_license}
                  onChange={(e) => editingProfessional 
                    ? setEditingProfessional({ ...editingProfessional, professional_license: e.target.value })
                    : setNewProfessionalForm({ ...newProfessionalForm, professional_license: e.target.value })
                  }
                />
                <Input
                  placeholder="Especialidad (opcional)"
                  value={editingProfessional ? editingProfessional.professional_specialty : newProfessionalForm.professional_specialty}
                  onChange={(e) => editingProfessional 
                    ? setEditingProfessional({ ...editingProfessional, professional_specialty: e.target.value })
                    : setNewProfessionalForm({ ...newProfessionalForm, professional_specialty: e.target.value })
                  }
                />
                <Input
                  placeholder="Biografía profesional (opcional)"
                  value={editingProfessional ? editingProfessional.professional_bio : newProfessionalForm.professional_bio}
                  onChange={(e) => editingProfessional 
                    ? setEditingProfessional({ ...editingProfessional, professional_bio: e.target.value })
                    : setNewProfessionalForm({ ...newProfessionalForm, professional_bio: e.target.value })
                  }
                />
                <div className="flex gap-2">
                  {editingProfessional ? (
                    <>
                      <Button 
                        variant="outline"
                        onClick={() => setEditingProfessional(null)}
                        className="flex-1"
                      >
                        Cancelar
                      </Button>
                      <Button 
                        onClick={() => updateProfessionalMutation.mutate({
                          id: editingProfessional.id,
                          data: {
                            full_name: editingProfessional.full_name,
                            professional_type: editingProfessional.professional_type,
                            professional_license: editingProfessional.professional_license,
                            professional_specialty: editingProfessional.professional_specialty,
                            professional_bio: editingProfessional.professional_bio,
                            is_professional: true,
                            professional_verified: true
                          }
                        })}
                        disabled={!editingProfessional.full_name || !editingProfessional.professional_type || !editingProfessional.professional_license}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                      >
                        Actualizar Profesional
                      </Button>
                    </>
                  ) : (
                    <Button 
                      onClick={() => createProfessionalMutation.mutate({
                        ...newProfessionalForm,
                        is_professional: true,
                        professional_verified: true
                      })}
                      disabled={!newProfessionalForm.full_name || !newProfessionalForm.email || !newProfessionalForm.professional_type || !newProfessionalForm.professional_license}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      Registrar Profesional
                    </Button>
                  )}
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Registrar Fabricante</h3>
              <div className="space-y-3">
                <Input
                  placeholder="Nombre del fabricante"
                  value={newManufacturerForm.name}
                  onChange={(e) => setNewManufacturerForm({ ...newManufacturerForm, name: e.target.value })}
                />
                <Input
                  placeholder="Marca"
                  value={newManufacturerForm.brand}
                  onChange={(e) => setNewManufacturerForm({ ...newManufacturerForm, brand: e.target.value })}
                />
                <Input
                  placeholder="Sitio web"
                  value={newManufacturerForm.website}
                  onChange={(e) => setNewManufacturerForm({ ...newManufacturerForm, website: e.target.value })}
                />
                <Input
                  type="email"
                  placeholder="Email de contacto"
                  value={newManufacturerForm.contact_email}
                  onChange={(e) => setNewManufacturerForm({ ...newManufacturerForm, contact_email: e.target.value })}
                />
                <Button 
                  onClick={() => createManufacturerMutation.mutate(newManufacturerForm)}
                  disabled={!newManufacturerForm.name || !newManufacturerForm.brand}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  Registrar Fabricante
                </Button>
              </div>
            </Card>

            {/* Edit Professional Modal */}
            {editingProfessional && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
                  <h3 className="font-semibold text-gray-900 mb-4">Editar Profesional</h3>
                  <div className="space-y-3">
                    <Input
                      placeholder="Nombre completo"
                      value={editingProfessional.full_name || ''}
                      onChange={(e) => setEditingProfessional({ ...editingProfessional, full_name: e.target.value })}
                    />
                    <Input
                      type="email"
                      placeholder="Email"
                      value={editingProfessional.email || ''}
                      disabled
                      className="bg-gray-100"
                    />
                    <select
                      value={editingProfessional.professional_type || ''}
                      onChange={(e) => setEditingProfessional({ ...editingProfessional, professional_type: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                    >
                      <option value="">Seleccionar tipo</option>
                      <option value="nutricionista">Nutricionista</option>
                      <option value="medico">Médico</option>
                      <option value="enfermero">Enfermero</option>
                      <option value="farmaceutico">Farmacéutico</option>
                      <option value="otro">Otro</option>
                    </select>
                    <Input
                      placeholder="Número de matrícula/licencia"
                      value={editingProfessional.professional_license || ''}
                      onChange={(e) => setEditingProfessional({ ...editingProfessional, professional_license: e.target.value })}
                    />
                    <Input
                      placeholder="Especialidad (opcional)"
                      value={editingProfessional.professional_specialty || ''}
                      onChange={(e) => setEditingProfessional({ ...editingProfessional, professional_specialty: e.target.value })}
                    />
                    <Input
                      placeholder="Biografía profesional (opcional)"
                      value={editingProfessional.professional_bio || ''}
                      onChange={(e) => setEditingProfessional({ ...editingProfessional, professional_bio: e.target.value })}
                    />
                    <div className="flex gap-2 pt-2">
                      <Button
                        variant="outline"
                        onClick={() => setEditingProfessional(null)}
                        className="flex-1"
                      >
                        Cancelar
                      </Button>
                      <Button
                        onClick={() => updateProfessionalMutation.mutate({
                          id: editingProfessional.id,
                          data: {
                            full_name: editingProfessional.full_name,
                            professional_type: editingProfessional.professional_type,
                            professional_license: editingProfessional.professional_license,
                            professional_specialty: editingProfessional.professional_specialty,
                            professional_bio: editingProfessional.professional_bio,
                            is_professional: true,
                            professional_verified: true
                          }
                        })}
                        disabled={!editingProfessional.full_name || !editingProfessional.professional_type || !editingProfessional.professional_license}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                      >
                        Guardar
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            <Card className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Invitar Usuario</h3>
              <div className="flex gap-3">
                <Input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="email@ejemplo.com"
                  className="flex-1"
                />
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="px-4 py-2 border border-gray-200 rounded-lg"
                >
                  <option value="user">Usuario</option>
                  <option value="admin">Admin</option>
                </select>
                <Button onClick={handleInviteUser} className="bg-indigo-600 hover:bg-indigo-700">
                  <Mail className="w-4 h-4 mr-2" />
                  Invitar
                </Button>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Lista de Usuarios</h3>
              <div className="space-y-3">
                {safeArray(users).filter(u => !u.is_professional).map((u) => (
                  <div key={u.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                        {u.full_name?.[0]?.toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{u.full_name}</p>
                        <p className="text-sm text-gray-500">{u.email}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {u.role === 'admin' && (
                            <span className="flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                              <Crown className="w-3 h-3" />
                              Admin
                            </span>
                          )}
                          <span className="text-xs text-gray-400">
                            {format(new Date(u.created_date), "d MMM yyyy", { locale: es })}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingUser(u)}
                      >
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (confirm('¿Estás seguro de eliminar este usuario?')) {
                            deleteUserMutation.mutate(u.id);
                          }
                        }}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Edit User Modal */}
            {editingUser && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="w-full max-w-md p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Editar Usuario</h3>
                  <div className="space-y-3">
                    <Input
                      placeholder="Nombre completo"
                      value={editingUser.full_name || ''}
                      onChange={(e) => setEditingUser({ ...editingUser, full_name: e.target.value })}
                    />
                    <Input
                      type="email"
                      placeholder="Email"
                      value={editingUser.email || ''}
                      disabled
                      className="bg-gray-100"
                    />
                    <select
                      value={editingUser.role || 'user'}
                      onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                    >
                      <option value="user">Usuario</option>
                      <option value="admin">Admin</option>
                    </select>
                    <div className="flex gap-2 pt-2">
                      <Button
                        variant="outline"
                        onClick={() => setEditingUser(null)}
                        className="flex-1"
                      >
                        Cancelar
                      </Button>
                      <Button
                        onClick={() => updateUserMutation.mutate({
                          id: editingUser.id,
                          data: {
                            full_name: editingUser.full_name,
                            role: editingUser.role
                          }
                        })}
                        className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                      >
                        Guardar
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Moderation Tab */}
          <TabsContent value="moderation" className="space-y-4">
            {(safeArray(pendingPosts).length + safeArray(pendingComments).length) === 0 && (
              <Card className="p-8 text-center">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-gray-600">No hay contenido pendiente de moderación</p>
              </Card>
            )}

            {safeArray(pendingPosts).length > 0 && (
              <Card className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Posts Pendientes ({safeArray(pendingPosts).length})</h3>
                <div className="space-y-3">
                  {safeArray(pendingPosts).map((post) => (
                    <div key={post.id} className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{post.title}</h4>
                          <p className="text-sm text-gray-600 mt-1">{post.content}</p>
                          <p className="text-xs text-gray-400 mt-2">Por {post.author_name}</p>
                          {post.flagged_for_medical_advice && (
                            <div className="flex items-center gap-1 mt-2 text-red-600 text-xs">
                              <AlertCircle className="w-3 h-3" />
                              Posible consejo médico no validado
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => updatePostMutation.mutate({ id: post.id, data: { status: 'approved' } })}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Aprobar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updatePostMutation.mutate({ id: post.id, data: { status: 'rejected' } })}
                          className="border-red-200 text-red-600 hover:bg-red-50"
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Rechazar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deletePostMutation.mutate(post.id)}
                          className="border-gray-200"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Eliminar
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {safeArray(pendingComments).length > 0 && (
              <Card className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Comentarios Pendientes ({safeArray(pendingComments).length})</h3>
                <div className="space-y-3">
                  {safeArray(pendingComments).map((comment) => (
                    <div key={comment.id} className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-gray-600">{comment.content}</p>
                      <p className="text-xs text-gray-400 mt-2">Por {comment.author_name}</p>
                      {comment.flagged_for_medical_advice && (
                        <div className="flex items-center gap-1 mt-2 text-red-600 text-xs">
                          <AlertCircle className="w-3 h-3" />
                          Posible consejo médico no validado
                        </div>
                      )}
                      <div className="flex gap-2 mt-3">
                        <Button
                          size="sm"
                          onClick={() => updateCommentMutation.mutate({ id: comment.id, data: { status: 'approved' } })}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Aprobar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateCommentMutation.mutate({ id: comment.id, data: { status: 'rejected' } })}
                          className="border-red-200 text-red-600 hover:bg-red-50"
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Rechazar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteCommentMutation.mutate(comment.id)}
                          className="border-gray-200"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {(safeArray(flaggedPosts).length > 0 || safeArray(flaggedComments).length > 0) && (
              <Card className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  Contenido Marcado ({safeArray(flaggedPosts).length + safeArray(flaggedComments).length})
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Este contenido fue marcado automáticamente por posibles consejos médicos no validados
                </p>
              </Card>
            )}
          </TabsContent>

          {/* Content Tab */}
          <TabsContent value="content" className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Productos Recientes</h3>
                <div className="space-y-3">
                  {safeArray(products).slice(0, 5).map((product) => (
                    <div key={product.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      {product.image_url ? (
                        <img src={product.image_url} alt={product.name} className="w-12 h-12 rounded-lg object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-gray-200" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{product.name}</p>
                        {product.brand && <p className="text-sm text-gray-500">{product.brand}</p>}
                      </div>
                      {product.scaneat_score && (
                        <div className={`
                          w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm
                          ${product.traffic_light === 'green' ? 'bg-emerald-50 text-emerald-700' :
                            product.traffic_light === 'yellow' ? 'bg-amber-50 text-amber-700' :
                            'bg-red-50 text-red-700'}
                        `}>
                          {product.scaneat_score}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Aditivos de Alto Riesgo</h3>
                <div className="space-y-3">
                  {safeArray(additives).filter(a => a.risk_level === 'alto').slice(0, 5).map((additive) => (
                    <div key={additive.id} className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
                      <div className="w-12 h-12 rounded-lg bg-red-100 text-red-700 flex items-center justify-center text-xs font-bold">
                        {additive.code}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{additive.name}</p>
                        <p className="text-xs text-gray-500">
                          En {safeArray(additive.product_ids).length} productos
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>

            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-gray-900">Base de Datos de Vitaminas</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Sincronizar vitaminas desde productos escaneados
                  </p>
                </div>
                <Button 
                  onClick={() => syncVitaminsMutation.mutate()}
                  disabled={syncVitaminsMutation.isPending}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {syncVitaminsMutation.isPending ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Sincronizando...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Sincronizar Vitaminas
                    </>
                  )}
                </Button>
              </div>
              
              <div className="grid md:grid-cols-2 gap-3">
                {safeArray(vitamins).slice(0, 6).map((vitamin) => {
                  const colors = {
                    alto: { bg: 'bg-emerald-50', text: 'text-emerald-700', badge: 'bg-emerald-100' },
                    moderado: { bg: 'bg-blue-50', text: 'text-blue-700', badge: 'bg-blue-100' },
                    bajo: { bg: 'bg-gray-50', text: 'text-gray-700', badge: 'bg-gray-100' }
                  }[vitamin.benefit_level] || { bg: 'bg-gray-50', text: 'text-gray-700', badge: 'bg-gray-100' };

                  return (
                    <div key={vitamin.id} className={`flex items-center gap-3 p-3 ${colors.bg} rounded-lg`}>
                      <div className={`w-12 h-12 rounded-lg ${colors.badge} ${colors.text} flex items-center justify-center text-xs font-bold`}>
                        {vitamin.code}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{vitamin.name}</p>
                        <p className="text-xs text-gray-500">
                          En {safeArray(vitamin.product_ids).length} productos
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            <div className="grid md:grid-cols-2 gap-4">
            </div>
          </TabsContent>

          {/* Legal Tab */}
          <TabsContent value="legal" className="space-y-6">
            <div className="grid gap-6">
              {safeArray(legalDocs).map((doc) => (
                <LegalDocumentEditor
                  key={doc.id}
                  document={doc}
                  onSave={async (updatedDoc) => {
                    await base44.entities.LegalDocument.update(doc.id, updatedDoc);
                    queryClient.invalidateQueries({ queryKey: ['legal-documents'] });
                    toast.success('Documento actualizado');
                  }}
                />
              ))}
            </div>
          </TabsContent>

          {/* Banners Tab */}
          <TabsContent value="banners">
            <BannerManager
              banners={banners}
              onUpdate={() => queryClient.invalidateQueries({ queryKey: ['banners'] })}
            />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card className="p-6">
              <h2 className="font-semibold text-gray-900 mb-4">Configuración de la App</h2>
              


              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Email de Contacto</label>
                  <Input
                    type="email"
                    value={settingsForm.contact_email || ''}
                    onChange={(e) => setSettingsForm({ ...settingsForm, contact_email: e.target.value })}
                    placeholder="contacto@escaneat.com"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Sitio Web</label>
                  <Input
                    value={settingsForm.website || ''}
                    onChange={(e) => setSettingsForm({ ...settingsForm, website: e.target.value })}
                    placeholder="https://escaneat.com"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Instagram (usuario sin @)</label>
                  <Input
                    value={settingsForm.instagram || ''}
                    onChange={(e) => setSettingsForm({ ...settingsForm, instagram: e.target.value })}
                    placeholder="escaneat"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Facebook (URL completa)</label>
                  <Input
                    value={settingsForm.facebook || ''}
                    onChange={(e) => setSettingsForm({ ...settingsForm, facebook: e.target.value })}
                    placeholder="https://facebook.com/escaneat"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Twitter/X (usuario sin @)</label>
                  <Input
                    value={settingsForm.twitter || ''}
                    onChange={(e) => setSettingsForm({ ...settingsForm, twitter: e.target.value })}
                    placeholder="escaneat"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">YouTube (URL completa o @usuario)</label>
                  <Input
                    value={settingsForm.youtube || ''}
                    onChange={(e) => setSettingsForm({ ...settingsForm, youtube: e.target.value })}
                    placeholder="https://youtube.com/@escaneat"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">LinkedIn (URL completa)</label>
                  <Input
                    value={settingsForm.linkedin || ''}
                    onChange={(e) => setSettingsForm({ ...settingsForm, linkedin: e.target.value })}
                    placeholder="https://linkedin.com/company/escaneat"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">WhatsApp (número con código de país)</label>
                  <Input
                    value={settingsForm.whatsapp || ''}
                    onChange={(e) => setSettingsForm({ ...settingsForm, whatsapp: e.target.value })}
                    placeholder="5491112345678"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Mensaje de Invitación por Email</label>
                  <textarea
                    value={settingsForm.invitation_message || ''}
                    onChange={(e) => setSettingsForm({ ...settingsForm, invitation_message: e.target.value })}
                    placeholder="¡Te invitamos a unirte a ScanEat! Descubre información nutricional detallada de los productos que consumes..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg min-h-[100px] text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">Este mensaje se enviará en los emails de invitación a nuevos usuarios</p>
                </div>
                <Button
                  onClick={() => saveSettingsMutation.mutate(settingsForm)}
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                  disabled={saveSettingsMutation.isPending}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saveSettingsMutation.isPending ? 'Guardando...' : 'Guardar Configuración'}
                </Button>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}